import { useState } from 'react';
import { residentTrends, type ApiClient, type ResidentTrendRow, type ResidentTrendsResponse } from '@aithora/shared';
import { ErrorNote } from '../../../ErrorNote';
import { useQuery } from '../../../useQuery';
import { A, sx } from '../parts';
import { PocDialog } from './PocDialog';
import { VITALS, residentLabel, type PocResident } from './pointOfCare';
import { CARD, NOTE, chip } from './styles';

const W = 700;
const H = 32;
const GAP = 12;

/** Bar heights in percent (25 to 100 between the week's low and high), as the design draws them; null stays null. */
export function barHeights(v: Array<number | null>): Array<number | null> {
  const xs = v.filter((x): x is number => x !== null);
  if (!xs.length) return v.map(() => null);
  const lo = Math.min(...xs);
  const sp = Math.max(...xs) - lo || 1;
  return v.map((x) => (x === null ? null : Math.round(25 + (75 * (x - lo)) / sp)));
}

/** One row's days as bars (design xTrRows), inline SVG; a day with nothing recorded has no bar. */
function Bars({ values }: { values: Array<number | null> }) {
  const h = barHeights(values);
  const n = values.length;
  const bw = (W - GAP * (n - 1)) / n;
  const last = values.length - 1;
  return (
    <svg aria-hidden="true" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" width="100%" height={H} style={{ display: 'block' }}>
      {h.map((p, i) =>
        p === null ? null : (
          <rect key={i} data-bar={i} x={i * (bw + GAP)} y={H - Math.max(3, (H * p) / 100)} width={bw} height={Math.max(3, (H * p) / 100)} rx={2} fill={i === last ? '#2F5C97' : 'rgba(47,92,151,.35)'} />
        ),
      )}
    </svg>
  );
}

const DEC: Record<ResidentTrendRow['key'], number> = { pulse: 0, bp_top: 0, temp: 1, meals: 0, fluids: 0 };
const fmt = (r: ResidentTrendRow, v: number) => `${v.toFixed(DEC[r.key])}${r.unit === '%' ? '%' : ` ${r.unit}`}`;

/** The newest day with a value, and which day it was ("72 bpm", or "72 bpm · Sat" when it was not today). */
export function latest(r: ResidentTrendRow, days: Array<{ label: string }>): string | null {
  for (let i = r.values.length - 1; i >= 0; i -= 1) {
    const v = r.values[i];
    if (v !== null) return i === r.values.length - 1 ? fmt(r, v) : `${fmt(r, v)} · ${days[i]?.label ?? ''}`;
  }
  return null;
}

/** Out of the usual range on its newest day, by the Chart's flag levels (pulse, BP top number, temperature). */
function outOfRange(t: ResidentTrendsResponse): string[] {
  const field = { pulse: 'pulse', bp_top: 'sys', temp: 'temp' } as const;
  const out: string[] = [];
  for (const r of t.rows) {
    const k = (field as Record<string, string>)[r.key];
    const f = k ? VITALS.find((x) => x.k === k) : null;
    const i = r.values.length - 1 - [...r.values].reverse().findIndex((v) => v !== null);
    const v = r.values[i];
    if (!f || v === null || v === undefined) continue;
    const flag = f.flag(v);
    if (flag) out.push(`${flag.toLowerCase()} (${fmt(r, v)}${i === r.values.length - 1 ? ' today' : ` on ${t.days[i]?.label}`})`);
  }
  return out;
}

/**
 * Resident trends (design pwLayer "trend", xTrRows): seven days of pulse, BP top number,
 * temperature, meals eaten and fluids for one resident, a bar a day.
 *
 *   GET /residents/:id/trends?days=7   a value per day in the building's time zone; null where
 *                                      nothing was recorded (staff charting and devices)
 */
export function TrendsSheet({ client, residents, onClose }: { client: ApiClient; residents: PocResident[]; onClose: () => void }) {
  const [who, setWho] = useState<string | null>(residents[0]?.id ?? null);
  const q = useQuery((signal) => residentTrends(client, { params: { id: who! }, query: { days: 7 }, signal }), [client, who], { enabled: !!who });
  const r = residents.find((x) => x.id === who) ?? null;
  // Never the last resident's week under a newly picked name.
  const t = q.data && q.data.residentId === who ? q.data : null;
  const flags = t ? outOfRange(t) : [];
  return (
    <PocDialog title="Resident trends" onClose={onClose} testId="poc-trends">
      {residents.length ? (
        <div role="group" aria-label="Resident" style={sx('display:flex;flex-wrap:wrap;gap:6px;')}>
          {residents.map((x) => (
            <A key={x.id} style={chip(who === x.id)} onClick={() => setWho(x.id)}>
              {residentLabel(x)}
            </A>
          ))}
        </div>
      ) : (
        <span role="status" style={sx('font-size:16px;color:#3C454F;')}>
          No residents are assigned to you this shift.
        </span>
      )}
      {q.error && <ErrorNote error={q.error} onRetry={q.reload} />}
      {who && !t && !q.error && <span style={sx('font-size:16px;color:#54606C;')}>Loading trends</span>}
      {t && (
        <div style={sx(CARD)} data-testid="trend-card">
          {t.rows.map((row) => {
            const now = latest(row, t.days);
            const any = row.values.some((v) => v !== null);
            return (
              <div key={row.key} data-testid={`trend-${row.key}`} style={sx('display:flex;flex-direction:column;gap:3px;padding:4px 0;')}>
                <div style={sx('display:flex;align-items:baseline;gap:8px;')}>
                  <span style={sx('flex:1 1 auto;font-size:16px;font-weight:700;color:#22272E;')}>{row.label}</span>
                  <span style={sx(`font-size:16px;font-weight:800;color:${now ? '#264C7E' : '#54606C'};`)}>{now ?? 'Nothing this week'}</span>
                </div>
                {any ? (
                  <>
                    <Bars values={row.values} />
                    <div aria-hidden="true" style={sx('display:flex;gap:5px;')}>
                      {t.days.map((d) => (
                        <span key={d.date} style={sx('flex:1 1 0;text-align:center;font-size:14px;font-weight:700;color:#54606C;')}>
                          {d.label}
                        </span>
                      ))}
                    </div>
                  </>
                ) : (
                  <span style={sx('font-size:16px;color:#54606C;line-height:1.4;')}>{row.key === 'meals' ? row.source : 'Nothing was recorded in the last 7 days.'}</span>
                )}
              </div>
            );
          })}
        </div>
      )}
      {t && flags.length > 0 && (
        <div role="status" data-testid="trend-flag" style={sx('padding:10px 12px;border-radius:10px;background:#FFFFFF;border:1px solid rgba(138,90,0,.45);font-size:16px;font-weight:700;color:#8A5A00;line-height:1.45;')}>
          {`${r?.name ?? 'This resident'}: ${flags.join(', ')}. This is outside the usual range. Tell the nurse.`}
        </div>
      )}
      {t && <span style={sx(NOTE)}>Last 7 days from what staff charted and what devices recorded. A day with no bar had nothing recorded.</span>}
    </PocDialog>
  );
}
